import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  NgZone,
  HostBinding,
} from '@angular/core';
import { Subject, fromEvent, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { Address, BookType } from '@bukio/viewer';

import { DialogService, ToastService } from 'shared/ui';

import { Book } from '../../models/book.model';

import { EventBusService } from '../../services/event-bus.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';

import { PDFPageSliderComponent } from '../pdf-page-slider/pdf-page-slider.component';
import { ThumbnailDialogComponent } from '../../dialogs/thumbnail-dialog/thumbnail-dialog.component';
import { ReadingGroupMembersDialogComponent } from '../../dialogs/reading-group-members-dialog/reading-group-members-dialog.component';
import { HelpDialogComponent } from '../../dialogs/help-dialog/help-dialog.component';
import { UIStateService } from '../../services/ui-state.service';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { ReadingGroup } from 'shared/models';
import {
  ReadingGroupMembersAPIService,
  ReadingGroupsAPIService,
  ViewerHistoryAPIService,
} from 'shared/services';
import { ActivatedRoute, Router } from '@angular/router';
import { ReadingGroupDialogService } from 'reading-group';
import { APIService } from '../../services/api.service';
import { lt } from 'semver';

export enum FooterMenuItem {
  BookHome,
  MyRecord,
  TOC,
  Thumbnail,
  Settings,
  Help,
  ReadingMode,
  GroupMembers,
  GroupMemos,
}

@Component({
  selector: 'viewer-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  @ViewChild(PDFPageSliderComponent) pdfPageSliderComp!: PDFPageSliderComponent;
  @ViewChild('pageInput') pageInputElement!: ElementRef<HTMLInputElement>;

  private _unsubscriber: Subject<void> = new Subject<void>();
  private _visibilityTimeout?: ReturnType<typeof setTimeout>;
  private _isBookHomeOpening = false;
  private readonly _minRequiredIOSVersion = '1.1.6';
  private readonly _minRequiredAndroidVersion = '1.1.6';

  public readonly MenuItem = FooterMenuItem;
  public readonly BookType = BookType;

  private _address?: Address;
  private _book?: Book;
  public _currentGroupName?: string;
  public _groups: ReadingGroup[] = [];

  public _groupMemberCount = 0;
  public _isCommentaryEditorMode = false;
  public _unreadMemoCount?: number;
  public _isOwnedByUser = false; // 미리보기인지 여부
  public _isUnderline = false;
  public _isVisible = true; // 모바일에서 footer visibility 제어
  public _isApp = false;

  get _bookType(): BookType | undefined {
    return this._book?.meta.type;
  }

  get isGroupReading(): boolean {
    return !!this._readingGroupsService.currentGroup;
  }

  get visibility(): string {
    // 데스크탑에서는 항상 visible
    if (typeof window !== 'undefined' && window.innerWidth >= 1200) {
      return 'visible';
    }
    // 모바일에서는 _isVisible 상태에 따라 제어
    return this._isVisible ? 'visible' : 'hidden';
  }

  @HostBinding('class.footer-hidden')
  get isFooterHidden(): boolean {
    // 모바일에서만 hidden 클래스 적용
    if (typeof window !== 'undefined' && window.innerWidth >= 1200) {
      return false;
    }
    return !this._isVisible;
  }

  constructor(
    private _eventBus: EventBusService,
    private _readingGroupsService: ReadingGroupsService,
    private _dialogService: DialogService,
    private _uiStateService: UIStateService,
    private _annotationsService: AnnotationsV2Service,
    private _readingGroupsAPIService: ReadingGroupsAPIService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _toastService: ToastService,
    private _ngZone: NgZone,
    private _readingGroupMembersAPIService: ReadingGroupMembersAPIService,
    private _readingGroupDialogService: ReadingGroupDialogService,
    private _viewerHistoryAPIService: ViewerHistoryAPIService,
    private _apiService: APIService
  ) {}

  ngOnInit(): void {
    this._isApp = !!(
      (window as any).webkit?.messageHandlers?.bukHybrid ||
      (window as any).bukAndroid
    );

    // 모바일에서 초기 상태 설정 (sidebar가 닫혀있으면 숨김)
    this._updateVisibility();

    this._activatedRoute.queryParams.subscribe((params) => {
      this._isUnderline = params['isUnderline'] === 'true';
    });

    this._readingGroupsService.currentGroup$.subscribe((group) => {
      this._groupMemberCount = group ? group.member_count : 0;
      this._unreadMemoCount = group?.memo_unread_count;

      this._currentGroupName = group?.name;
    });

    this._eventBus
      .on('ContentsComponent:bookLoad')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ book }) => {
        this._book = book;
        this._isCommentaryEditorMode = this._uiStateService.isCommentaryEditor;
        this._isOwnedByUser = !!book.isOwnedByUser;

        this._readingGroupsAPIService
          .getJoined(0, 100, book.meta.bid)
          .pipe(takeUntil(this._unsubscriber))
          .subscribe((response) => {
            this._groups = response.items;
          });
      });

    this._eventBus
      .on('ContentsComponent:addressChange')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        this._address = data.address;
      });

    // 모바일에서 sidebar 열림/닫힘 상태에 따라 footer visibility 제어
    this._eventBus
      .on('SidebarContainerComponent:opened')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ position }) => {
        if (position === 'bottom') {
          // 열릴 때는 비동기로 처리하여 댓글창 열림에 영향 없도록
          if (this._visibilityTimeout) {
            clearTimeout(this._visibilityTimeout);
            this._visibilityTimeout = undefined;
          }
          // requestAnimationFrame을 사용하여 댓글창 열림이 먼저 실행되도록
          requestAnimationFrame(() => {
            this._updateVisibility();
          });
        }
      });

    this._eventBus
      .on('SidebarContainerComponent:closed')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ position }) => {
        if (position === 'bottom') {
          // 닫힐 때는 애니메이션 완료 후(300ms) hidden으로 변경
          if (this._visibilityTimeout) {
            clearTimeout(this._visibilityTimeout);
          }
          this._visibilityTimeout = setTimeout(() => {
            this._updateVisibility();
            this._visibilityTimeout = undefined;
          }, 300); // sidebar 애니메이션 시간과 동일
        }
      });

    // 화면 크기 변경 시 visibility 업데이트
    this._ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(takeUntil(this._unsubscriber))
        .subscribe(() => {
          this._ngZone.run(() => {
            this._updateVisibility();
          });
        });
    });
  }

  ngOnDestroy(): void {
    if (this._visibilityTimeout) {
      clearTimeout(this._visibilityTimeout);
    }
    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  private _updateVisibility(): void {
    // 데스크탑에서는 항상 visible, 모바일에서만 제어
    if (window.innerWidth >= 1200) {
      this._isVisible = true;
    } else {
      // 모바일에서는 sidebar의 active 상태 확인
      const sidebarElement = document.querySelector(
        '.sidebar-mobile[position="bottom"]'
      );
      this._isVisible = sidebarElement?.classList.contains('active') ?? false;
    }
  }

  // 1. 브릿지 존재 여부를 확인하는 헬퍼 함수 (전역 또는 클래스 내부)
  private checkBridgeAvailable(): boolean {
    const appWin = window as any;
    const hasAndroidBridge =
      typeof appWin.bukAndroid?.openBrowser === 'function';
    const hasIosBridge =
      typeof appWin.webkit?.messageHandlers?.bukHybrid?.postMessage ===
      'function';

    return hasAndroidBridge || hasIosBridge;
  }

  _onMenuItemClick(menu: FooterMenuItem): void {
    switch (menu) {
      case FooterMenuItem.GroupMembers:
        this._openGroupMembersDialog();
        break;
      case FooterMenuItem.TOC:
      case FooterMenuItem.Settings:
      case FooterMenuItem.GroupMemos:
        this._eventBus.fire('FooterComponent:menuClick', { menu });
        break;
      case FooterMenuItem.Thumbnail:
        this._openThumbnailDialog();
        break;
      case FooterMenuItem.Help:
        this._openHelpDialog();
        break;
      case FooterMenuItem.MyRecord:
        this._eventBus.fire(
          'AnnotationSettingsFabComponent:anotationsButtonClick'
        );
        break;
      case FooterMenuItem.ReadingMode:
        this._openReadingModeDialog();
        break;
      case FooterMenuItem.BookHome:
        if (
          this._isApp &&
          this.checkBridgeAvailable() &&
          this._shouldUseAppTokenRedirect()
        ) {
          if (this._isBookHomeOpening) {
            return;
          }

          this._isBookHomeOpening = true;
          this._apiService.getAppToken().subscribe({
            next: (result) => {
              const appToken = result.app_token;
              const redirectTo = this._book?.meta.parent_bid
                ? `https://buk.io/@${this._book?.meta.parent_bid}`
                : `https://buk.io/@${this._book?.meta.bid}`;

              const redirectUrl = `${
                location.origin
              }/api/v1/app/redirect?app_token=${encodeURIComponent(
                appToken
              )}&redirect_to=${encodeURIComponent(redirectTo)}`;

              this._isBookHomeOpening = false;

              const appWindow = window as Window & {
                bukAndroid?: {
                  openBrowser?: (url: string) => void;
                };
                webkit?: {
                  messageHandlers?: {
                    bukHybrid?: {
                      postMessage?: (payload: {
                        type: string;
                        data: string;
                      }) => void;
                    };
                  };
                };
              };
              if (typeof appWindow.bukAndroid?.openBrowser === 'function') {
                appWindow.bukAndroid.openBrowser(redirectUrl);
              } else if (
                appWindow.webkit?.messageHandlers?.bukHybrid?.postMessage
              ) {
                appWindow.webkit.messageHandlers.bukHybrid.postMessage({
                  type: 'openBrowser',
                  data: redirectUrl,
                });
              } else {
                window.open(redirectUrl, '_blank');
              }
            },
            error: () => {
              this._isBookHomeOpening = false;
              this._toastService.open('인증에 실패했습니다.');
            },
          });
        } else if (this._isApp) {
          const redirectTo = this._book?.meta.parent_bid
            ? `https://buk.io/@${this._book?.meta.parent_bid}`
            : `https://buk.io/@${this._book?.meta.bid}`;
          window.open(redirectTo, '_blank');
        } else {
          const bookHomeBid =
            this._book?.meta.parent_bid || this._book?.meta.bid;
          const isEmbedded =
            typeof window !== 'undefined' && window.self !== window.top;

          if (isEmbedded) {
            const urlTree = this._router.createUrlTree(['@' + bookHomeBid]);
            const relativeUrl = this._router.serializeUrl(urlTree);
            const absoluteUrl = new URL(relativeUrl, window.location.href).href;
            window.open(absoluteUrl, '_blank');
          } else {
            this._router.navigate(['@' + bookHomeBid]);
          }
        }

        break;
    }
  }

  private _shouldUseAppTokenRedirect(): boolean {
    const appVersion = this._getAppVersionFromUserAgent();
    if (!appVersion) {
      return false;
    }

    const minRequiredVersion =
      this._getAppOsFromUserAgent() === 'ios'
        ? this._minRequiredIOSVersion
        : this._minRequiredAndroidVersion;

    return !lt(appVersion, minRequiredVersion);
  }

  private _getAppVersionFromUserAgent(): string | undefined {
    const matched = /BukHybridApp\/([^\s]+)(?: \((iOS|Android)\))?/.exec(
      navigator.userAgent
    );

    return matched?.[1];
  }

  private _getAppOsFromUserAgent(): 'android' | 'ios' {
    const matched = /BukHybridApp\/([^\s]+)(?: \((iOS|Android)\))?/.exec(
      navigator.userAgent
    );

    if (matched?.[2]) {
      return matched[2] === 'iOS' ? 'ios' : 'android';
    }

    return (/iPad|iPhone|iPod/.test(navigator.platform) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !(window as any).MSStream
      ? 'ios'
      : 'android';
  }

  private _openReadingModeDialog(): void {
    if (!this._book) {
      return;
    }

    const dialog = this._readingGroupDialogService.openReadingModeSelection(
      this._book.meta.bid,
      this._book.meta.title,
      this._book.meta.cover,
      this._book.meta.author
    );

    dialog.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      if (result.canceled) {
        return;
      }

      this._goToViewer(result.groupId);
    });
  }

  private _goToViewer(groupId?: number): void {
    if (!this._book) {
      return;
    }

    this._viewerHistoryAPIService
      .getLastReadPage(this._book.meta.bid)
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        this._readingGroupsAPIService
          .getJoined(0, 100, this._book!.meta.bid)
          .pipe(takeUntil(this._unsubscriber))
          .subscribe((response) => {
            this._groups = response.items;
          });

        this._eventBus.fire('ReadingModeDialogComponent:groupChanged', {
          exited: null,
          entered: groupId ?? null,
        });

        this._eventBus.fire(
          'ReadingGroupMembersDialogComponent:memberDeselected'
        );

        this._annotationsService.setState({ groupId: groupId });
      });
  }

  private _openGroupMembersDialog(): void {
    if (!this._book || !this._isOwnedByUser) {
      return;
    }

    // 그룹 멤버 팝업이 열리기 전에 footer에 그룹원수 갱신을 위해 요청
    this._readingGroupMembersAPIService
      .getJoined(this._readingGroupsService.currentGroup!.id)
      .subscribe((response) => {
        this._groupMemberCount = response.length;
      });

    this._dialogService.open(ReadingGroupMembersDialogComponent, {
      data: {
        bid: this._book?.meta.bid,
      },
    });
  }

  _openThumbnailDialog(): void {
    if (!this._book || !this._address) {
      return;
    }

    this._dialogService.open(ThumbnailDialogComponent, {
      data: {
        book: this._book,
        currentIid: this._address.iid || this._book.items[0].iid,
      },
    });
  }

  _openHelpDialog(): void {
    if (!this._book) {
      return;
    }

    this._dialogService.open(HelpDialogComponent, {
      data: {
        isPDFBook: this._book.meta.type === BookType.PDF,
      },
    });
  }

  _onClickReadingGroup(group: ReadingGroup): void {
    // 선택한 그룹을 배열의 맨 앞으로 이동
    const groupIndex = this._groups.findIndex((g) => g.id === group.id);
    if (groupIndex > 0) {
      // 배열에서 제거하고 맨 앞에 추가
      this._groups.splice(groupIndex, 1);
      this._groups.unshift(group);
    }

    this._readingGroupsAPIService.touch(group.id).subscribe(() => {
      this._annotationsService.setState({
        groupId: group.id,
        memberId: undefined, // 그룹 변경 시 멤버 선택 해제
      });

      this._eventBus.fire('ReadingModeDialogComponent:groupChanged', {
        exited: null,
        entered: group.id ?? null,
      });

      this._eventBus.fire(
        'ReadingGroupMembersDialogComponent:memberDeselected'
      );

      this._annotationsService.setState({ groupId: group.id });

      // 그룹책 보기로 전환 토스트 메시지
      this._toastService.open(
        `'${group.name}' 그룹책 보기 모드로 변경되었습니다.`
      );
    });
  }

  _changeToMyBookViewer(): void {
    this._eventBus.fire('ReadingModeDialogComponent:groupChanged', {
      exited: null,
      entered: null,
    });

    this._eventBus.fire('ReadingGroupMembersDialogComponent:memberDeselected');

    this._annotationsService.setState({ groupId: undefined });

    // 나의책 보기로 전환 토스트 메시지
    this._toastService.open('나의책 보기 모드로 변경되었습니다.');
  }
}
